import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { SpeakerHigh, Copy, Check, Gift } from "@phosphor-icons/react";

const STATUS_PT = {
  pending: "Aguardando pagamento", in_process: "Em análise", approved: "Aprovado",
  rejected: "Recusado", cancelled: "Cancelado", expired: "Expirado",
};

const fmtHours = (s) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}min`;
};

function PixBox({ purchase }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(purchase.qr_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Código Pix copiado");
  };
  return (
    <div className="space-y-3">
      <div className="text-center">
        <div className="text-3xl font-black tracking-tighter text-[#0F4C3A]">R$ {purchase.amount_brl.toFixed(2).replace(".", ",")}</div>
        <div className="text-xs mt-1 text-[#5C5C59]" data-testid="transcription-pix-status">Status: <b>{STATUS_PT[purchase.status] || purchase.status}</b></div>
      </div>
      {purchase.qr_code_base64 && (
        <img src={`data:image/png;base64,${purchase.qr_code_base64}`} alt="QR Pix" className="w-56 h-56 mx-auto border border-[#E5E5E0]" data-testid="transcription-pix-qr" />
      )}
      {purchase.qr_code && (
        <div className="flex items-center gap-2">
          <Input readOnly value={purchase.qr_code} data-testid="transcription-pix-code" className="rounded-none text-[10px] font-mono" />
          <Button onClick={copy} data-testid="transcription-pix-copy" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
            {copied ? <Check size={14} weight="bold" /> : <Copy size={14} weight="bold" />}
          </Button>
        </div>
      )}
      <p className="text-[11px] text-center text-[#5C5C59]">Aguardando pagamento… As horas entram automaticamente após a confirmação.</p>
    </div>
  );
}

export const TranscriptionCredits = ({ canBuy }) => {
  const [credits, setCredits] = useState(null);
  const [open, setOpen] = useState(false);
  const [pack, setPack] = useState("3h");
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const load = () => api.get("/transcription/credits").then(({ data }) => setCredits(data)).catch(() => {});
  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, []);

  const stopPoll = () => { clearInterval(pollRef.current); pollRef.current = null; };

  const buy = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/transcription/purchase", { package: pack });
      setPurchase(data);
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/transcription/purchase/${data.purchase_id}`);
          setPurchase(r.data);
          if (r.data.status === "approved") {
            stopPoll();
            toast.success("Pagamento confirmado! Horas adicionadas.");
            setOpen(false);
            load();
          }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!credits) return null;
  return (
    <div className="border border-[#E5E5E0] bg-white p-5 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="transcription-credits-card">
      <div className="flex items-center gap-4">
        <div className="h-11 w-11 bg-[#EDF3F0] flex items-center justify-center text-[#0F4C3A]"><SpeakerHigh size={22} weight="duotone" /></div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#5C5C59]">Transcrição de áudio</div>
          <div className="font-mono text-2xl font-black text-[#0F4C3A]" data-testid="transcription-total-left">{fmtHours(credits.total_seconds_left)}</div>
          <div className="text-[11px] text-[#5C5C59] flex items-center gap-1">
            <Gift size={12} /> <span data-testid="transcription-free-left">{fmtHours(credits.free_seconds_left)}</span> grátis neste mês · <span data-testid="transcription-paid-left">{fmtHours(credits.paid_seconds_left)}</span> compradas
          </div>
        </div>
      </div>
      {canBuy && (
        <Button onClick={() => { setPurchase(null); setOpen(true); }} data-testid="transcription-buy-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
          Comprar horas
        </Button>
      )}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) stopPoll(); }}>
        <DialogContent className="bg-white rounded-none max-w-md">
          <DialogHeader><DialogTitle>Comprar horas de transcrição</DialogTitle></DialogHeader>
          {purchase ? <PixBox purchase={purchase} /> : (
            <div className="space-y-2">
              {credits.packages.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPack(p.key)}
                  data-testid={`transcription-pack-${p.key}`}
                  className={`w-full flex items-center justify-between px-4 py-3 border transition-colors ${pack === p.key ? "border-[#0F4C3A] bg-[#EDF3F0]" : "border-[#E5E5E0] hover:border-[#0F4C3A]/50"}`}
                >
                  <span className="font-bold">{p.hours} horas</span>
                  <span className="font-mono">R$ {p.price_brl.toFixed(2).replace(".", ",")}</span>
                </button>
              ))}
              <p className="text-[10px] text-[#5C5C59]">Todo mês, na renovação, você ganha 1 hora grátis. Horas compradas não expiram.</p>
            </div>
          )}
          <DialogFooter>
            {!purchase && (
              <Button onClick={buy} disabled={loading} data-testid="transcription-generate-pix-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
                {loading ? "Gerando…" : "Gerar Pix"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
